import React, { useState } from "react";

/**
 * Anatomical muscle map. Stylized fit-physique silhouette (V-taper torso,
 * sculpted shoulders/quads/lats) rendered as front + back SVG views. The
 * primary muscle is painted in Electric Blaze red, synergists in a
 * desaturated red, and the rest of the body in a dim baseline grey.
 *
 * Highlight rules:
 *   primaryMuscle    → solid red — the muscle this exercise targets.
 *   secondaryMuscles → desaturated red — synergists primed by this lift's
 *                       movement pattern (mirrors lib/warmups.js so the
 *                       UI matches what the warm-up logic believes).
 *   everything else  → idle grey.
 */

const LABELS = {
    chest: "Chest (pectoralis major)",
    back: "Back (lats / rhomboids / rear delts)",
    shoulders: "Shoulders (front + medial delts)",
    biceps: "Biceps (biceps brachii + brachialis)",
    triceps: "Triceps (all three heads)",
    quads: "Quads (rectus femoris + vastus group)",
    hamstrings: "Hamstrings (biceps femoris + semis)",
    glutes: "Glutes (gluteus maximus + medius)",
    adductors: "Adductors (inner thigh)",
    calves: "Calves (gastroc + soleus)",
    abs: "Abs (rectus abdominis + obliques)",
    forearms: "Forearms (brachioradialis + flexors)",
    traps: "Traps (upper trapezius)",
};

const SECONDARIES = {
    horizontal_push: ["triceps", "shoulders"],
    vertical_push: ["triceps"],
    horizontal_pull: ["biceps"],
    vertical_pull: ["biceps"],
    lateral_raise: [],
    elbow_flexion: ["forearms"],
    elbow_extension: [],
    squat: ["glutes", "hamstrings", "adductors"],
    hip_hinge: ["glutes", "hamstrings"],
    knee_extension: [],
    knee_flexion: ["glutes"],
    hip_extension: ["hamstrings"],
    hip_abduction: [],
    hip_adduction: [],
    ankle_extension: [],
    trunk_flexion: [],
};

// Color tokens — kept inline so the component is theme-agnostic.
const C = {
    silhouette: "#0d0d0d",
    silhouetteEdge: "#272727",
    detail: "#1a1a1a",
    idle: "#262626",
    idleEdge: "#3a3a3a",
    synergist: "#7a1f1c",
    primary: "#FF3B30",
};

export default function MuscleMap({ primary, pattern = "", className = "" }) {
    const [hovered, setHovered] = useState(null);
    const secondary = new Set(SECONDARIES[pattern] || []);
    secondary.delete(primary);

    const fillFor = (key) => {
        if (key === primary) return C.primary;
        if (secondary.has(key)) return C.synergist;
        return C.idle;
    };
    const strokeFor = (key) =>
        key === primary || secondary.has(key) ? "#0a0a0a" : C.idleEdge;

    const Region = ({ id, children }) => (
        <g
            data-muscle={id}
            data-testid={`muscle-region-${id}`}
            onMouseEnter={() => setHovered(id)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(id)}
            onBlur={() => setHovered(null)}
            tabIndex={0}
            style={{ cursor: "help", outline: "none" }}
        >
            {children}
        </g>
    );

    const tooltip = hovered ? LABELS[hovered] : LABELS[primary];

    return (
        <div className={`tactical-card p-5 ${className}`} data-testid="muscle-map">
            <div className="flex items-baseline justify-between">
                <div className="label-eyebrow text-accent-red">Target map</div>
                <div className="label-eyebrow text-muted-foreground">Front · Back</div>
            </div>

            <svg
                viewBox="0 0 260 340"
                className="mt-3 w-full"
                role="img"
                aria-label={`Muscle map highlighting ${LABELS[primary] || primary}`}
            >
                {/* ===================== FRONT VIEW ===================== */}
                <g transform="translate(0,0)">
                    {/* Silhouette: V-taper torso, sculpted shoulders, defined legs */}
                    <path
                        d="
                            M 65 18
                            Q 65 8 75 8
                            Q 85 8 85 18
                            L 85 28
                            Q 92 30 94 36
                            L 96 42
                            Q 108 44 116 56
                            Q 120 64 118 76
                            L 116 90
                            Q 113 96 109 100
                            L 108 122
                            Q 107 132 102 138
                            L 99 154
                            L 95 162
                            L 93 178
                            L 96 198
                            L 100 240
                            L 96 282
                            L 90 318
                            L 80 318
                            L 76 282
                            L 74 250
                            L 72 230
                            L 70 250
                            L 68 282
                            L 64 318
                            L 54 318
                            L 50 282
                            L 46 240
                            L 50 198
                            L 53 178
                            L 51 162
                            L 47 154
                            L 44 138
                            Q 39 132 38 122
                            L 37 100
                            Q 33 96 30 90
                            L 28 76
                            Q 26 64 30 56
                            Q 38 44 50 42
                            L 52 36
                            Q 54 30 61 28
                            L 61 18 Z
                        "
                        fill={C.silhouette}
                        stroke={C.silhouetteEdge}
                        strokeWidth="1"
                    />

                    {/* Trapezius (upper traps — slope from neck to shoulders) */}
                    <Region id="traps">
                        <path
                            d="M 61 28 L 75 30 L 85 28 L 92 36 L 75 38 L 54 36 Z"
                            fill={fillFor("traps")}
                            stroke={strokeFor("traps")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Deltoids — front cap on each shoulder */}
                    <Region id="shoulders">
                        <path
                            d="M 52 42 Q 36 46 30 60 Q 30 70 36 76 Q 42 70 46 60 Q 50 50 52 42 Z"
                            fill={fillFor("shoulders")}
                            stroke={strokeFor("shoulders")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 94 42 Q 110 46 116 60 Q 116 70 110 76 Q 104 70 100 60 Q 96 50 94 42 Z"
                            fill={fillFor("shoulders")}
                            stroke={strokeFor("shoulders")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Pectorals — twin shelves meeting at sternum */}
                    <Region id="chest">
                        <path
                            d="M 73 45 Q 60 47 52 54 Q 48 64 52 76 Q 62 82 73 80 Z"
                            fill={fillFor("chest")}
                            stroke={strokeFor("chest")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 73 45 Q 86 47 94 54 Q 98 64 94 76 Q 84 82 73 80 Z"
                            fill={fillFor("chest")}
                            stroke={strokeFor("chest")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Biceps — teardrop with peak */}
                    <Region id="biceps">
                        <path
                            d="M 32 78 Q 26 88 28 102 Q 32 110 38 108 Q 42 96 40 84 Z"
                            fill={fillFor("biceps")}
                            stroke={strokeFor("biceps")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 114 78 Q 120 88 118 102 Q 114 110 108 108 Q 104 96 106 84 Z"
                            fill={fillFor("biceps")}
                            stroke={strokeFor("biceps")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Forearms — brachioradialis flare */}
                    <Region id="forearms">
                        <path
                            d="M 28 108 Q 24 120 26 134 L 32 138 Q 36 124 36 110 Z"
                            fill={fillFor("forearms")}
                            stroke={strokeFor("forearms")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 118 108 Q 122 120 120 134 L 114 138 Q 110 124 110 110 Z"
                            fill={fillFor("forearms")}
                            stroke={strokeFor("forearms")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Abs — 6-pack rectus abdominis + obliques */}
                    <Region id="abs">
                        <g>
                            {/* Six-pack — three rows, two columns, divided by linea alba */}
                            <rect x="64" y="86" width="9" height="9" rx="1.5" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" />
                            <rect x="74" y="86" width="9" height="9" rx="1.5" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" />
                            <rect x="64" y="97" width="9" height="9" rx="1.5" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" />
                            <rect x="74" y="97" width="9" height="9" rx="1.5" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" />
                            <rect x="64" y="108" width="9" height="11" rx="1.5" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" />
                            <rect x="74" y="108" width="9" height="11" rx="1.5" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" />
                            {/* Serratus / obliques flanking */}
                            <path d="M 56 86 Q 52 100 58 122 L 62 118 L 62 90 Z" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" opacity="0.85" />
                            <path d="M 91 86 Q 95 100 89 122 L 85 118 L 85 90 Z" fill={fillFor("abs")} stroke={strokeFor("abs")} strokeWidth="0.5" opacity="0.85" />
                        </g>
                    </Region>

                    {/* Quads — three heads visible: rectus femoris (center), vastus
                        lateralis (outer sweep), vastus medialis (teardrop near knee). */}
                    <Region id="quads">
                        {/* LEFT leg */}
                        <path
                            d="M 53 168 Q 45 184 48 218 L 56 228 L 58 200 L 60 178 Z"
                            fill={fillFor("quads")}
                            stroke={strokeFor("quads")}
                            strokeWidth="0.5"
                        />
                        <path
                            d="M 60 168 Q 62 190 60 222 L 68 225 L 70 200 L 68 175 Z"
                            fill={fillFor("quads")}
                            stroke={strokeFor("quads")}
                            strokeWidth="0.5"
                        />
                        <path
                            d="M 56 218 Q 60 226 66 226 L 64 232 L 56 228 Z"
                            fill={fillFor("quads")}
                            stroke={strokeFor("quads")}
                            strokeWidth="0.5"
                            opacity="0.92"
                        />
                        {/* RIGHT leg */}
                        <path
                            d="M 93 168 Q 101 184 98 218 L 90 228 L 88 200 L 86 178 Z"
                            fill={fillFor("quads")}
                            stroke={strokeFor("quads")}
                            strokeWidth="0.5"
                        />
                        <path
                            d="M 86 168 Q 84 190 86 222 L 78 225 L 76 200 L 78 175 Z"
                            fill={fillFor("quads")}
                            stroke={strokeFor("quads")}
                            strokeWidth="0.5"
                        />
                        <path
                            d="M 90 218 Q 86 226 80 226 L 82 232 L 90 228 Z"
                            fill={fillFor("quads")}
                            stroke={strokeFor("quads")}
                            strokeWidth="0.5"
                            opacity="0.92"
                        />
                    </Region>

                    {/* Adductors — inner thigh strip */}
                    <Region id="adductors">
                        <path
                            d="M 68 170 L 78 170 L 76 224 L 70 224 Z"
                            fill={fillFor("adductors")}
                            stroke={strokeFor("adductors")}
                            strokeWidth="0.5"
                        />
                    </Region>

                    {/* Calves (front: tibialis anterior — dim baseline) */}
                    <Region id="calves">
                        <path
                            d="M 56 248 Q 50 270 54 296 L 60 296 L 62 270 Z"
                            fill={fillFor("calves")}
                            stroke={strokeFor("calves")}
                            strokeWidth="0.5"
                            opacity="0.55"
                        />
                        <path
                            d="M 90 248 Q 96 270 92 296 L 86 296 L 84 270 Z"
                            fill={fillFor("calves")}
                            stroke={strokeFor("calves")}
                            strokeWidth="0.5"
                            opacity="0.55"
                        />
                    </Region>

                    {/* Subtle sternum / centerline detail */}
                    <line x1="73" y1="46" x2="73" y2="80" stroke={C.detail} strokeWidth="0.7" />
                    <line x1="73" y1="86" x2="73" y2="120" stroke={C.detail} strokeWidth="0.7" />

                    <text x="73" y="334" textAnchor="middle" className="label-eyebrow" fill="#777" style={{ fontSize: 7, letterSpacing: 1 }}>FRONT</text>
                </g>

                {/* ===================== BACK VIEW ===================== */}
                <g transform="translate(130,0)">
                    {/* Same V-taper silhouette mirrored for back */}
                    <path
                        d="
                            M 65 18
                            Q 65 8 75 8
                            Q 85 8 85 18
                            L 85 28
                            Q 92 30 94 36
                            L 96 42
                            Q 108 44 116 56
                            Q 120 64 118 76
                            L 116 90
                            Q 113 96 109 100
                            L 108 122
                            Q 107 132 102 138
                            L 99 154
                            L 95 162
                            L 93 178
                            L 96 198
                            L 100 240
                            L 96 282
                            L 90 318
                            L 80 318
                            L 76 282
                            L 74 250
                            L 72 230
                            L 70 250
                            L 68 282
                            L 64 318
                            L 54 318
                            L 50 282
                            L 46 240
                            L 50 198
                            L 53 178
                            L 51 162
                            L 47 154
                            L 44 138
                            Q 39 132 38 122
                            L 37 100
                            Q 33 96 30 90
                            L 28 76
                            Q 26 64 30 56
                            Q 38 44 50 42
                            L 52 36
                            Q 54 30 61 28
                            L 61 18 Z
                        "
                        fill={C.silhouette}
                        stroke={C.silhouetteEdge}
                        strokeWidth="1"
                    />

                    {/* Trapezius — diamond shape from neck out to shoulders, down to mid-back */}
                    <Region id="traps">
                        <path
                            d="M 73 30 L 92 38 L 84 70 L 73 76 L 62 70 L 54 38 Z"
                            fill={fillFor("traps")}
                            stroke={strokeFor("traps")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Rear deltoids */}
                    <Region id="shoulders">
                        <path
                            d="M 52 42 Q 36 46 30 60 Q 30 70 36 76 Q 42 70 46 60 Q 50 50 52 42 Z"
                            fill={fillFor("shoulders")}
                            stroke={strokeFor("shoulders")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 94 42 Q 110 46 116 60 Q 116 70 110 76 Q 104 70 100 60 Q 96 50 94 42 Z"
                            fill={fillFor("shoulders")}
                            stroke={strokeFor("shoulders")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Back — lats + rhomboids forming the classic V-sweep into the waist */}
                    <Region id="back">
                        <path
                            d="
                                M 52 60
                                Q 46 80 50 108
                                Q 56 124 62 132
                                L 73 130
                                L 84 132
                                Q 90 124 96 108
                                Q 100 80 94 60
                                L 86 76
                                L 73 80
                                L 60 76 Z
                            "
                            fill={fillFor("back")}
                            stroke={strokeFor("back")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Triceps — horseshoe (long + lateral + medial heads visible) */}
                    <Region id="triceps">
                        <path
                            d="M 32 78 Q 24 92 28 108 Q 34 112 40 108 Q 42 92 40 84 Z"
                            fill={fillFor("triceps")}
                            stroke={strokeFor("triceps")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 114 78 Q 122 92 118 108 Q 112 112 106 108 Q 104 92 106 84 Z"
                            fill={fillFor("triceps")}
                            stroke={strokeFor("triceps")}
                            strokeWidth="0.6"
                        />
                        {/* Horseshoe split lines */}
                        <line x1="33" y1="86" x2="34" y2="104" stroke={C.detail} strokeWidth="0.6" />
                        <line x1="113" y1="86" x2="112" y2="104" stroke={C.detail} strokeWidth="0.6" />
                    </Region>

                    {/* Forearms — back view */}
                    <Region id="forearms">
                        <path
                            d="M 26 108 Q 22 122 26 138 L 32 138 Q 36 124 34 110 Z"
                            fill={fillFor("forearms")}
                            stroke={strokeFor("forearms")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 120 108 Q 124 122 120 138 L 114 138 Q 110 124 112 110 Z"
                            fill={fillFor("forearms")}
                            stroke={strokeFor("forearms")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Glutes — two rounded hemispheres */}
                    <Region id="glutes">
                        <path
                            d="M 52 142 Q 50 160 58 174 Q 68 180 73 172 L 73 142 Z"
                            fill={fillFor("glutes")}
                            stroke={strokeFor("glutes")}
                            strokeWidth="0.6"
                        />
                        <path
                            d="M 94 142 Q 96 160 88 174 Q 78 180 73 172 L 73 142 Z"
                            fill={fillFor("glutes")}
                            stroke={strokeFor("glutes")}
                            strokeWidth="0.6"
                        />
                    </Region>

                    {/* Hamstrings — biceps femoris (outer) + semi-tendinosus (inner) */}
                    <Region id="hamstrings">
                        {/* LEFT leg */}
                        <path
                            d="M 50 182 Q 46 200 50 224 L 58 228 L 60 200 L 60 182 Z"
                            fill={fillFor("hamstrings")}
                            stroke={strokeFor("hamstrings")}
                            strokeWidth="0.5"
                        />
                        <path
                            d="M 63 182 Q 62 200 64 224 L 70 224 L 70 200 L 68 182 Z"
                            fill={fillFor("hamstrings")}
                            stroke={strokeFor("hamstrings")}
                            strokeWidth="0.5"
                        />
                        {/* RIGHT leg */}
                        <path
                            d="M 96 182 Q 100 200 96 224 L 88 228 L 86 200 L 86 182 Z"
                            fill={fillFor("hamstrings")}
                            stroke={strokeFor("hamstrings")}
                            strokeWidth="0.5"
                        />
                        <path
                            d="M 83 182 Q 84 200 82 224 L 76 224 L 76 200 L 78 182 Z"
                            fill={fillFor("hamstrings")}
                            stroke={strokeFor("hamstrings")}
                            strokeWidth="0.5"
                        />
                    </Region>

                    {/* Calves — gastrocnemius diamond + soleus base */}
                    <Region id="calves">
                        <path
                            d="
                                M 55 244
                                Q 48 260 50 280
                                L 56 286
                                Q 60 282 62 270
                                L 64 250
                                Z
                            "
                            fill={fillFor("calves")}
                            stroke={strokeFor("calves")}
                            strokeWidth="0.5"
                        />
                        <path
                            d="
                                M 91 244
                                Q 98 260 96 280
                                L 90 286
                                Q 86 282 84 270
                                L 82 250
                                Z
                            "
                            fill={fillFor("calves")}
                            stroke={strokeFor("calves")}
                            strokeWidth="0.5"
                        />
                    </Region>

                    {/* Spine + lat split detail lines */}
                    <line x1="73" y1="46" x2="73" y2="138" stroke={C.detail} strokeWidth="0.7" />

                    <text x="73" y="334" textAnchor="middle" className="label-eyebrow" fill="#777" style={{ fontSize: 7, letterSpacing: 1 }}>BACK</text>
                </g>
            </svg>

            <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground" data-testid="muscle-tooltip">
                    {hovered ? (
                        <span><span className="text-accent-red font-bold uppercase">Hover · </span>{tooltip}</span>
                    ) : (
                        <span><span className="text-accent-red font-bold uppercase">Target · </span>{tooltip}</span>
                    )}
                </div>
            </div>
            <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2" style={{ background: C.primary }} /> Primary</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2" style={{ background: C.synergist }} /> Synergist</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 border" style={{ background: C.idle, borderColor: C.idleEdge }} /> Idle</span>
            </div>
        </div>
    );
}
